/**
 * Comment moderation service — the Brooklyn admin comments list, comment
 * update, and the moderation actions offered per status.
 *
 * The status vocabulary itself (int ↔ enum name ↔ label) lives in `./status`,
 * the table shared with the /moderation page, and is re-exported from this
 * barrel so every existing importer keeps working unchanged.
 *
 * AI_FLAGGED (6) is the moderation queue status, written by Brooklyn's CO-568
 * worker when the classifier holds a comment for human review. It reaches this
 * legacy REST page only partially, on purpose:
 *
 * - Brooklyn's admin list endpoint (`/v1/admin/comments/list`) still
 *   Joi-validates `?status` as 0..5, so the legacy page does NOT offer an
 *   "AI flagged" filter option until Brooklyn widens that allow-list.
 * - That endpoint's legacy `?status=1 -> IN(1,3)` shim deliberately excludes 6:
 *   flagged rows belong to the /moderation page, not the backlog view.
 * - Publish / Reject from a 6 row already work, because the admin only ever
 *   writes the target status (0 or 4) through the comment PUT.
 * @module services/comments
 */
import { fromJS } from 'immutable'
import get from 'lodash/get'
import request from '../request'
import { createAuthorization } from '../auth'
import {
  STATUS_AI_FLAGGED,
  STATUS_AI_REJECTED,
  STATUS_NEEDS_REVIEW,
  STATUS_PENDING_REVIEW,
  STATUS_PUBLISHED,
  STATUS_REJECTED,
  STATUS_UNPUBLISHED,
} from './status'

export {
  STATUS_AI_FLAGGED,
  STATUS_AI_REJECTED,
  STATUS_LABELS,
  STATUS_NEEDS_REVIEW,
  STATUS_PENDING_REVIEW,
  STATUS_PUBLISHED,
  STATUS_REJECTED,
  STATUS_UNPUBLISHED,
  getStatusLabel,
} from './status'

/**
 * The moderation transitions offered for each status, one explicit case per
 * status. There is deliberately NO fallback: an unrecognised status offers no
 * actions rather than guessing a transition on the moderator's behalf.
 *
 * UNPUBLISHED (1) keeps both Publish and Reject. That is how the legacy backlog
 * gets triaged into the new vocabulary over time, since those rows were never
 * migrated and no migration could tell "never reviewed" from "rejected".
 *
 * AI_REJECTED (5) and AI_FLAGGED (6) offer the same pair as PENDING_REVIEW: the
 * human either overturns the machine (Publish) or confirms it, and confirming
 * converts the decision into a human REJECTED (4). No admin action writes 5 or 6.
 */
export const STATUS_ACTIONS = {
  [STATUS_PUBLISHED]: [{ label: 'Unpublish', status: STATUS_UNPUBLISHED }],
  [STATUS_PENDING_REVIEW]: [
    { label: 'Publish', status: STATUS_PUBLISHED },
    { label: 'Reject', status: STATUS_REJECTED },
  ],
  [STATUS_REJECTED]: [{ label: 'Publish', status: STATUS_PUBLISHED }],
  [STATUS_AI_REJECTED]: [
    { label: 'Publish', status: STATUS_PUBLISHED },
    { label: 'Reject', status: STATUS_REJECTED },
  ],
  [STATUS_AI_FLAGGED]: [
    { label: 'Publish', status: STATUS_PUBLISHED },
    { label: 'Reject', status: STATUS_REJECTED },
  ],
  [STATUS_UNPUBLISHED]: [
    { label: 'Publish', status: STATUS_PUBLISHED },
    { label: 'Reject', status: STATUS_REJECTED },
  ],
  [STATUS_NEEDS_REVIEW]: [
    { label: 'Publish', status: STATUS_PUBLISHED },
    { label: 'Unpublish', status: STATUS_UNPUBLISHED },
  ],
}

/**
 * Get the moderation actions available for a comment status
 * @param {number} status The comment status
 * @returns {Array<{label: string, status: number}>} The available actions; empty for an unrecognised status
 */
export function getStatusActions (status) {
  return STATUS_ACTIONS[status] || []
}

/**
 * Get a list of comments
 * @param {object} options The options
 * @param {string} options.server The server url
 * @param {Map} options.auth An immutable map with the auth data
 * @param {number} options.offset The query offset
 * @param {number} options.limit The query limit
 */
export async function getCommentsList (options = {}) {
  const { server, auth, offset, limit, uid, contentId, status } = options
  const res = await request
    .get(`${server}/v1/admin/comments/list`)
    .query({
      offset,
      limit,
      uid,
      contentId,
      status,
    })
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return fromJS(get(res, 'body', {}))
}

/**
 * Update a comment, allows partial update
 * @param {object} options The options
 * @param {string} options.server The server url
 * @param {Map} options.auth An immutable map with the auth data
 * @param {number} options.id The comment id
 * @param {Map} options.data The PUT request body
 */
export async function update (options = {}) {
  const { server, auth, id, data } = options
  const res = await request
    .put(`${server}/v1/comments/${id}`)
    .send(data.toJS())
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return fromJS(get(res, 'body', {}))
}
